<template>
  <div class="px-3 main-wrapper bg-main">
    <div class="mt-3">
      <h3 class="page-title">{{ $t("deposit.deposit_form") }}</h3>
    </div>

    <div class="card-body">
      <div
        class="error-box"
        v-if="error"
      >{{ error }}</div>

      <!-- CURRENCY SELECT -->
      <div class="form-group">
        <label class="label">{{ $t('deposit.choose_currency') }}</label>

        <input
          type="text"
          readonly
          class="input"
          @click="showCurrencyPicker = true"
          :value="selectedCurrency?.name || ''"
        />

        <popup
          v-model:show="showCurrencyPicker"
          position="bottom"
          round
          destroy-on-close
        >
          <picker
            show-toolbar
            :title="$t('deposit.choose_currency')"
            :cancel-button-text="$t('cancel')"
            :confirm-button-text="$t('confirm')"
            :columns="currencyColumns"
            @confirm="onSelectCurrency"
            @cancel="showCurrencyPicker = false"
          />
        </popup>
        <span class="input-error">{{ errors.currency }}</span>
      </div>

      <!-- Account SELECT -->
      <div
        class="form-group"
        v-if="selectedCurrency"
      >
        <label class="label">{{ $t('deposit.choose_payment_type') }}</label>

        <input
          type="text"
          readonly
          class="input"
          @click="showAccountPicker = true"
          placeholder="Bank / Wallet"
          :value="selectedAccount?.name || ''"
        />

        <popup
          v-model:show="showAccountPicker"
          position="bottom"
          round
        >
          <picker
            show-toolbar
            :title="$t('deposit.choose_payment_type')"
            :cancel-button-text="$t('cancel')"
            :confirm-button-text="$t('confirm')"
            :columns="accountColumns"
            @confirm="onSelectAccount"
            @cancel="showAccountPicker = false"
          />
        </popup>

        <span class="input-error">{{ errors.account }}</span>

        <AccountCard
          class="mt-3"
          v-if="selectedAccount"
          :account="selectedAccount"
        />
      </div>


      <div class="form-group">
        <label class="label">{{ $t("deposit.amount") }}</label>
        <input
          type="number"
          class="input"
          v-model="depositData.amount"
        />
        <span class="input-error">{{ errors.amount }}</span>

        <CurrencyOptions
          class="mt-3"
          v-if="selectedCurrency"
          :options="selectedCurrency.options"
          :currency-code="selectedCurrency.code"
          v-model="depositData.amount"
        />
        <!-- Combined Exchange Info -->
        <div
          v-if="depositData.amount && selectedCurrency"
          class="currency-info"
        >
          <div class="rate">
            1 {{ selectedCurrency.code }} = <strong>{{ selectedCurrency.rate }}</strong> ฿
          </div>
          <div class="amount">
            {{ $t('deposit.you_will_get') }}: <strong>{{ convertedAmount }}</strong> ฿
          </div>
        </div>

      </div>

      <div class="form-group mt-4">
        <label class="label">{{ $t('deposit.last_transaction_id') }} (6 digits)</label>
        <input
          type="number"
          class="input"
          v-model="depositData.remark"
        />
        <span class="input-error">{{ errors.remark }}</span>
      </div>

      <div
        class="form-group"
        v-if="userActivePromotions.length"
      >
        <label
          class="label"
          for="promotion_id"
        >Promotion</label>
        <v-select
          :options="userActivePromotions"
          label="name"
          v-model="depositData.promotion_id"
          :reduce="(promotion) => promotion.id"
          class="input"
        >
          <template v-slot:option="option">
            <div class="d-flex align-items-center">
              <img
                :src="option.image"
                style="width: 50px; border-radius: 2px;"
              />
              {{ option.name }}
            </div>
          </template>
        </v-select>
        <span class="text-danger">
          {{ errors.promotion_id }}
        </span>
      </div>

      <button
        class="submit-btn"
        @click="sendDepositData"
      >
        <div v-if="!isConfirm">{{ $t("deposit.submit") }}</div>
        <Loading
          v-else
          size="24"
        />
      </button>

    </div>
  </div>
</template>


<script>
import AccountCard from "@/components/deposit/AccountCard.vue";
import CurrencyOptions from "@/components/deposit/CurrencyOptions.vue";
import { Popup, Picker, Toast, Loading } from "vant";
import { mapActions, mapState } from "vuex";

export default {
  components: {
    Popup,
    Picker,
    Loading,
    AccountCard,
    CurrencyOptions
  },

  data() {
    return {
      currencies: [],
      selectedCurrency: null,
      accounts: [],
      selectedAccount: null,

      showCurrencyPicker: false,
      showAccountPicker: false,

      currencyColumns: [],

      depositData: {
        amount: null,
        remark: "",
      },

      errors: {},
      error: null,
      isConfirm: false,
    };
  },

  computed: {
    ...mapState('promotions', ['userActivePromotions']),

    currentTime() {
      return moment(this.createDateWithTimeZone()).format("lll");
    },
    accountColumns() {
      if (!this.selectedCurrency) return [];

      return this.accounts
        .filter(a => a.currency_id === this.selectedCurrency.id)
        .map(a => ({
          text: a.name,
          value: a.id,
        }));
    },
    convertedAmount() {
      if (!this.selectedCurrency || !this.depositData.amount) return 0;

      const amount = this.depositData.amount * this.selectedCurrency.rate;
      return parseFloat(amount.toFixed(8));
    }
  },

  methods: {
    ...mapActions('promotions', ['fetchUserActivePromotions']),
    ...mapActions(["fetchUser"]),

    createDateWithTimeZone() {
      const moment = require("moment-timezone");
      const dateInTimeZone = moment.tz(new Date(), "Asia/Rangoon");
      const year = dateInTimeZone.year();
      const month = dateInTimeZone.month();
      const day = dateInTimeZone.date();
      const hour = dateInTimeZone.hour();
      const minute = dateInTimeZone.minute();
      const second = dateInTimeZone.second();
      const millisecond = dateInTimeZone.millisecond();

      const newDateObject = new Date(
        year,
        month,
        day,
        hour,
        minute,
        second,
        millisecond
      );
      return newDateObject;
    },

    async fetchCurrencies() {
      const res = await axios.get("/currencies");
      this.currencies = res.data.data;

      this.currencyColumns = this.currencies.map(c => ({
        text: `${c.name} (${c.code})`,
        value: c.id,
      }));
    },

    async fetchAccounts() {
      const res = await axios.get('/accounts');
      this.accounts = res.data.data;
    },

    onSelectCurrency({ value }) {
      this.selectedCurrency = this.currencies.find(c => c.id === value);

      this.depositData.amount = null;
      this.selectedAccount = null;
      this.depositData.currency_id = value;
      this.depositData.account_id = null;

      this.showCurrencyPicker = false;
    },

    onSelectAccount({ value }) {
      this.selectedAccount = this.accounts.find(a => a.id === value);
      this.showAccountPicker = false;
    },

    copyText() {
      navigator.clipboard.writeText(this.selectedAccount.number);
      Toast.success("Copied");
    },

    validateDeposit() {
      this.error = null;
      const err = {};

      if (!this.selectedCurrency) {
        err.currency = this.$t('deposit.currency_error');
      }

      if (!this.selectedAccount) {
        err.account = this.$t('deposit.enter_account_type');
      }

      if (!this.depositData.amount) {
        err.amount = this.$t('deposit.enter_amount');
      } else if (
        this.selectedCurrency &&
        this.depositData.amount < this.selectedCurrency.minimum_deposit
      ) {
        err.amount = this.$t('deposit.minimum_error', {
          amount: this.selectedCurrency.minimum_deposit
        });
      }

      if (!this.depositData.remark || this.depositData.remark.length < 6) {
        err.remark = this.$t('deposit.remark_error');
      }

      this.errors = err;

      return Object.keys(err).length === 0;
    },

    async sendDepositData() {
      if (!this.validateDeposit()) {
        Toast.fail("Failed!");
        return false;
      }

      this.isConfirm = true;

      const payload = {
        currency_id: this.selectedCurrency.id,
        account_id: this.selectedAccount.id,
        amount: this.depositData.amount,
        remark: this.depositData.remark,
        promotion_id: this.depositData.promotion_id,
        date: this.currentTime
      };

      try {
        await axios.post(
          "/deposits",
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            }
          }
        );

        Toast.success("Success");

        await this.fetchUserActivePromotions();

        this.depositData = { amount: null, remark: "", promotion_id: null };
        this.selectedAccount = null;
        this.selectedCurrency = null;

      } catch (e) {
        const message =
          e?.response?.data?.message ||
          e?.response?.data ||
          "Something went wrong, please contact admin";

        this.error = message;
        Toast.fail("Failed");
      }

      this.isConfirm = false;
    },
  },

  async mounted() {
    this.$store.commit("setLoading", true);
    try {
      const authUser = await this.fetchUser();
      if (!authUser) {
        this.$router.push("/auth/login");
        return;
      }

      await this.fetchUserActivePromotions();
      await this.fetchCurrencies();
      await this.fetchAccounts();
    } finally {
      this.$store.commit("setLoading", false);
    }
  },
};
</script>


<style scoped>
.page-title {
  color: white;
  text-align: center;
  font-size: 22px;
  font-weight: 600;
}

.form-group {
  margin-top: 3rem;
  margin-bottom: 3rem;
}

.label {
  color: #fff;
  font-size: 1rem;
}

.input {
  width: 100%;
  padding: 12px 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
}

.input-error {
  color: #ff7070;
  font-size: 12px;
}

.submit-btn {
  width: 100%;
  margin-top: 25px;
  padding: 14px;
  border-radius: 50px;
  background: linear-gradient(to right, #5A4BFF, #A557FF);
  color: white;
  font-size: 20px;
  border: none;
}

.currency-info {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px;
  border-radius: 6px;
  margin-top: 10px;
}

.currency-info .rate {
  font-size: 13px;
  color: #aaa;
}

.currency-info .amount {
  margin-top: 4px;
  font-size: 14px;
  color: #fff;
}

.currency-info strong {
  color: #ffd700;
}
</style>
